// React.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import dayjs from "dayjs";
import ReactTooltip from "react-tooltip";
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import { ActivityHashData, InstanceOptions, StatisticsResultsTableProps } from "./types";
import ExecutionPlan from "../../../types/instance/ExecutionPlan";

// Helper.
import Helper from '../../../helpers/Helper';
import {
    archiverUrl,
    fetchResults,
    fetchWithAuthorization,
    getAggregateInt, getTimeZone,
    getToolTipPosition,
} from '../../../helpers/utils';
import { showError } from '../../../utils/showAlert';
import ConditionalRender from "../../../helpers/ConditionalRender";

// Context
import { InstanceContext } from "../../../context/InstanceContext";

// Components.
import Alert from "../../../component/Alert";
import StatementToolTip from "../../../component/instance/table/StatementToolTip";
import SingleStatementChart from "./single-statement-chart";
import StatementExecutionPlans from "./statements-execution-plans";
import SearchInput from '../../../component/SearchInput';


const StatementsTable = (props: StatisticsResultsTableProps) => {
    const searchParams = new URLSearchParams(window.location.search)

    const timezone = getTimeZone()
    const { instances } = useContext(InstanceContext);
    const apiVersion = parseInt(searchParams.get('api_version') || '', 10) || 1
    const [statementsData, setStatementsData] = useState<ActivityHashData[]>([]);
    const [tableFilter, setTableFilter] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [visibleToolTip, setVisibleToolTip] = useState({ id: '', position: 0 });
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [options, setOptions] = useState<InstanceOptions[]>(props.instanceOptions)
    const [executionPlans, setExecutionPlans] = useState<ExecutionPlan[][]>([]);
    const [executionPlansLoaded, setExecutionPlansLoaded] = useState<string[]>([]);
    const [allChanges, setAllChanges] = useState<any[][]>([])

    const { statements } = props

    // Update this if data that are not in topN should not be updated
    const withOutsideTopNData = true
    ReactTooltip.rebuild();

    useEffect(() => {
        setOptions(props.instanceOptions)
    }, [props.generateId])

    useEffect(() => {
        const allChanges = [0, 1].map(index => [
            ...(executionPlans[index] || []),
            ...(props.combinedChanges[index] || []),
        ]);
        setAllChanges(allChanges);
    }, [executionPlans, props.combinedChanges]);

    const getInstanceType = (id: number) => {
        const instanceFound = instances.find(instance => instance.id === id)
        return instanceFound ? instanceFound.type : 'unknown'
    }

    const aggregateInt = useMemo(() => getAggregateInt(props.options.aggregate, props.instanceOptions, instances), [props.options.aggregate])

    const toggleRow = (rowId: string): void => {
        setExecutionPlansLoaded(prev => {
            if (prev.includes(rowId)) {
                // If the rowId exists, remove it
                return prev.filter((id: string) => id !== rowId);
            }
            return prev;
        });

        setExpandedRows(prev => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const sortWithoutEmptyValues = (rowA: any, rowB: any, columnId: string, desc: boolean | undefined) => {
        const valueA = rowA.values[columnId];
        const valueB = rowB.values[columnId];

        const numA = Number(valueA);
        const numB = Number(valueB);

        const isEmptyA = valueA == null || valueA === '' || Number.isNaN(numA);
        const isEmptyB = valueB == null || valueB === '' || Number.isNaN(numB);

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA) return desc ? -1 : 1;
        if (isEmptyB) return desc ? 1 : -1; 

        const absA = Math.abs(numA);
        const absB = Math.abs(numB);

        return absA > absB ? 1 : absA < absB ? -1 : 0;
    };

    const columns: Column[] = useMemo(() => {
        return [
            {
                Header: '#',
                accessor: 'sqlhash',
                headerClassName: 'text',
                className: 'concatenate-large',
                canSort: true,
                Cell: ({ cell }: any) => {
                    const customId = props.options.showSqlId ? cell.row.original.sqlid : cell.row.original.sqlhash;
                    // @ts-ignore
                    const getColor = props.statementColors[customId];
                    const isExpanded = expandedRows[cell.row.id];  // Check if this row is expanded

                    return (
                        <div onClick={() => toggleRow(cell.row.id)} style={{ cursor: 'pointer' }}>
                            <span className="mx-2 fw-bold" style={{
                                width: '8px',
                                display: 'inline-block',
                                textAlign: 'center'
                            }}>{isExpanded ? '- ' : '+ '}</span>

                            <span className="me-1 ps-1" style={{ backgroundColor: getColor }}>&nbsp;</span>

                            <span style={{ marginRight: '5px' }}></span>

                            {props.options.showSqlId && cell.row.original.sqlid ? cell.row.original.sqlid : cell.row.values.sqlhash}
                        </div>
                    );
                }
            },
            {
                Header: 'Statement',
                accessor: 'sqltext',
                headerClassName: 'text',
                className: 'concatenate-large',
                canSort: true,
                Cell: ({ cell }: any) => <p className='statement-text'>{cell.row.values.sqltext}</p>
            },
            {
                Header: 'Wait Time',
                accessor: 'waittime',
                headerClassName: 'concatenate-medium text-end grey',
                className: 'concatenate-medium text-end grey',
                canSort: true,
                sortDescFirst: true,
                sortType: (rowA, rowB, columnId, desc) => sortWithoutEmptyValues(rowA, rowB, columnId, desc),
                Cell: ({ cell }: any) =>
                    <React.Fragment>
                        {cell.row.original.waittime !== undefined ? Helper.getTimeInEnglish(cell.row.original.waittime) : '-'}
                    </React.Fragment>
            },
            {
                Header: 'Wait Time',
                accessor: `waittime_1`,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end',
                canSort: true,
                sortType: (rowA, rowB, columnId, desc) => sortWithoutEmptyValues(rowA, rowB, columnId, desc),
                sortDescFirst: true,
                Cell: ({ cell }) =>
                    <React.Fragment>
                        {cell.row.values[`waittime_1`] !== undefined ? Helper.getTimeInEnglish(cell.row.values[`waittime_1`]) : '-'}
                    </React.Fragment>
            },
            {
                Header: 'Difference (time)',
                accessor: `difference`,
                headerClassName: 'concatenate-medium text-end gray-bg',
                className: 'concatenate-medium text-end gray-bg',
                canSort: true,
                sortDescFirst: true,
                sortType: (rowA, rowB, columnId, desc) => sortWithoutEmptyValues(rowA, rowB, columnId, desc),
                Cell: ({ cell }) => {
                    return (
                        <React.Fragment>
                            {/* Display time difference*/}
                            {cell.row.values.difference === 0 ? `0ms` : Helper.getTimeInEnglish(cell.row.values.difference)}

                            {/* Tooltip and change icon */}
                            <i className={Helper.getChangeIcon(cell.row.values.waittime, cell.row.values[`waittime_1`])}/>
                        </React.Fragment>
                    );
                }
            },
            {
                Header: 'Difference (%)',
                accessor: `percentage`,
                headerClassName: 'concatenate-medium text-end',
                className: 'concatenate-medium text-end ',
                disableSortBy: true,
                Cell: ({ cell }) => {

                    // Check if the number has decimals
                    const hasDecimals = cell.row.values.percentage % 1 !== 0;
                    const percentValue = hasDecimals ? `${cell.row.values.percentage.toFixed(2)}%` : `${cell.row.values.percentage}%`

                    return (
                        <React.Fragment>
                            {/* Display percentage difference */}
                            {isNaN(cell.row.values.percentage) ? '-' : percentValue}
                            {cell.row.values.percentage ? <i className={Helper.getChangeIcon(cell.row.values.waittime, cell.row.values[`waittime_1`])}/> : ''}
                        </React.Fragment>
                    );
                }
            },
        ]
    }, [props.generateId, props.statementColors, expandedRows, statementsData]);

    const getMissingData = async(instanceOption: InstanceOptions, sqlhash: string) => {
        try {
            const apiTagString = `${archiverUrl(apiVersion)}/activity/sql?`
            const from = dayjs(instanceOption.periodStart).format('YYYY-MM-DD+HH:mm')
            const to = dayjs(instanceOption.periodEnd).format('YYYY-MM-DD+HH:mm')
            const results = await fetchResults([fetchWithAuthorization(`${apiTagString}from=${from}&to=${to}&tz=${timezone}&id=${instanceOption.instanceId}&interval=${aggregateInt}&sort=${props.options.metric}+desc&type=${instanceOption.instanceType}&limit=${props.options.topN}&sqlhash=${sqlhash}`)], false)
            return results[0]
        } catch (error: any) {
            console.error('Failed to get missing statement data:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load missing statement data', errorMessage);
            return [];
        }
    }

    const getPercentageAndDifference = (val1: number, val2: number, val0: number) => {
        const difference: number = (val2 || 0) - (val1 || 0)
        let percentage = 0
        if (val1 === undefined) {
            percentage = NaN
        } else if (val2 === undefined) {
            percentage = 100
        } else {
            percentage = Math.abs((difference / val0) * 100);
        }

        return {
            difference,
            percentage
        }
    }

    const concatArrays = async(list: any[]) => {
        try {
            const arr: ActivityHashData[] = []
            list.forEach((item, idx: number) => {
                item.forEach((subItem: any) => {
                    if (idx === 0) {
                        arr.push({
                            ...subItem,
                            instanceId: options[idx].instanceId,
                            periodStart: options[idx].periodStart,
                            periodEnd: options[idx].periodEnd,
                        })
                    } else {
                        const atIndex: number = arr.findIndex(data => data.sqlhash === subItem.sqlhash)
                        const statementExist: any = atIndex > -1 ? arr[atIndex] : {
                            sqlhash: subItem.sqlhash,
                            sqltext: subItem.sqltext,
                            sqlid: subItem.sqlid,
                        }
                        statementExist[`waittime_${idx}`] = subItem.waittime
                        statementExist[`executions_${idx}`] = subItem.executions
                        statementExist.instanceId = options[idx].instanceId
                        statementExist.periodStart = options[idx].periodStart
                        statementExist.periodEnd = options[idx].periodEnd

                        const diff = getPercentageAndDifference(statementExist.waittime, statementExist.waittime_1, statementExist.waittime)
                        statementExist['difference'] = diff.difference
                        statementExist['percentage'] = diff.percentage

                        if (atIndex === -1) {
                            arr.push(statementExist)
                        }
                    }
                })
            })
            if (withOutsideTopNData) {
                await updateArrWithMissingData(arr)
            } else {
                setLoading(false)
                setStatementsData(arr)
            }
        } catch (error: any) {
            console.error('Failed to concat arrays:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to process statement data', errorMessage);
            setLoading(false);
            setStatementsData([]);
        }
    }

    const updateArrWithMissingData = async(list: any[]) => {
        try {
            const missingDataPromises: Promise<any>[] = []

            list.forEach((item) => {

                if (!item.executions || !item.waittime) {
                    missingDataPromises.push(getMissingData(props.instanceOptions[0], item.sqlhash).then(data => {

                        if (data?.length) {
                            item.waittime = data[0].waittime
                            item.executions = data[0].executions
                        }
                    }))
                }
                if (!item.executions_1 || !item.waittime_1) {
                    missingDataPromises.push(getMissingData(props.instanceOptions[1], item.sqlhash).then(data => {
                        if (data?.length) {
                            item.waittime_1 = data[0].waittime
                            item.executions_1 = data[0].executions
                        }
                    }))
                }
            })

            await Promise.all(missingDataPromises)

            // Calculate the differences and percentages after data is fetched
            list.forEach(item => {

                const diff = getPercentageAndDifference(item.waittime, item.waittime_1, item.waittime)
                item['difference'] = diff.difference
                item['percentage'] = diff.percentage
            })

            setLoading(false)
            setStatementsData(list)
        } catch (error: any) {
            console.error('Failed to update array with missing data:', error);
            const errorMessage = error.message || error.toString();
            showError('Failed to update missing statement data', errorMessage);
            setLoading(false);
            setStatementsData(list); // Still set the data we have
        }
    }

    useEffect(() => {
        if (statements.length === options.length) {
            void concatArrays(statements)
        }
    }, [statements])

    useEffect(() => {
        setLoading(true)
        setExpandedRows({})
    }, [props.generateId])

    const data = useMemo(() => {
        const filteredData = (statementsData.length && statementsData.filter((row) =>
            (row.sqltext?.toLowerCase().includes(tableFilter.toLowerCase())) ||
            (row.sqlid?.toString().toLowerCase().includes(tableFilter.toLowerCase())) ||
            (row.sqlhash?.toLowerCase().includes(tableFilter.toLowerCase())))) || []

        filteredData.forEach((row) => {
            row.sqltext = row.sqltext?.replace(/"/gi, '\'') || ''
        })

        return filteredData
    }, [tableFilter, statementsData]);

    const getInstanceName = (id: any) => {
        const instance = instances.find(instance => instance.id === parseInt(id))
        return instance ? instance.name : ''
    }

    const getCsvData = useCallback((csvData: any[]) => {
        // Transform the CSV data that TableCSVExport already prepared (which is sorted)
        // Format the difference column same as UI: "0ms" for 0, otherwise use Helper.getTimeInEnglish
        return csvData.map((row: any) => {
            const originalRow = row._original || row;
            
            // Format difference column same as UI
            let differenceFormatted = '-';
            if (originalRow.difference !== undefined && originalRow.difference !== null) {
                if (originalRow.difference === 0) {
                    differenceFormatted = '0ms';
                } else {
                    differenceFormatted = Helper.getTimeInEnglish(originalRow.difference);
                }
            }
            
            return {
                ...row,
                instance: getInstanceName(originalRow.instanceId),
                sqlhash: `'${originalRow.sqlhash}'`,
                sqlid: `'${originalRow.sqlid}'`,
                waittime: originalRow.waittime !== undefined ? Helper.getTimeInEnglish(originalRow.waittime) : '-',
                waittime_1: originalRow.waittime_1 !== undefined ? Helper.getTimeInEnglish(originalRow.waittime_1) : '-',
                difference: differenceFormatted,
                percentage: originalRow.percentage !== undefined && !isNaN(originalRow.percentage) 
                    ? `${originalRow.percentage.toFixed(2)}%` 
                    : '-',
                sqltext: originalRow.sqltext,
            };
        });
    }, [instances]);



    const tableInstance = useTable(
        {
            columns,
            data,
            initialState:
                {
                    sortBy: [
                        {
                            id: 'difference',
                            desc: true,
                        }
                    ],
                    pageIndex: 0,
                    pageSize: 200,
                },
            disableSortRemove: false
        }, useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page
    } = tableInstance;

    function clearTableFilter() {
        setTableFilter('');
    }

    const setPlans = (plans: ExecutionPlan[][], rowId: string) => {
        setExecutionPlans(plans);
        setExecutionPlansLoaded(prev => {
            const currentPlans = prev || [];
            return [...currentPlans, rowId];
        });
    };

    return (
        <div className="card">
            <div className="card-header">
                Statements
                <span className="badge bg-info"
                      data-tip="Total Statements">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseStatements" aria-expanded="false" aria-controls="collapseStatements"/>
            </div>
            <div id="collapseStatements" className="card-body collapse show">
                {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 &&
                    (<Alert
                        message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by the number of executions.`}
                        heading="Statement records" variant="alert-info"/>)}
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                            tableInstance={tableInstance}
                            filename="Statements"
                            transformData={getCsvData}
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <div className="table-responsive mt-4">
                    <ConditionalRender if={!loading && data.length}>
                        <table {...getTableProps()} className="table">
                            <thead>
                            <tr>
                                <td/>
                                <td/>
                                {options.map((instance, index) => (
                                    <td className={`text-center fw-bold ${index % 2 === 0 ? 'gray-bg' : ''}`}>
                                        {Helper.getFormattedPeriodHeadingFromDate(instance.periodStart, instance.periodEnd)}
                                    </td>
                                ))}
                            </tr>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className={(column as any).headerClassName}>
                                            {column.render('Header')}
                                            {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                    <i className="fal fa-sort-amount-up-alt"></i> :
                                                    <i className="fal fa-sort-amount-down-alt"></i>) :
                                                <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                            {page.map((row) => {
                                prepareRow(row);
                                const isExpanded = expandedRows[row.id];

                                return (
                                    // @ts-ignore
                                    <React.Fragment>
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map((cell: any) => (
                                                <td {...cell.getCellProps()} className={(cell.column as any).className}>
                                                    {cell.column.id === "sqltext" ? (
                                                        <div
                                                            className='tooltip-scroll ellipsis'
                                                            onClick={() => toggleRow(cell.row.id)} // Toggle expand/collapse on click
                                                            onMouseEnter={(event) =>
                                                                setVisibleToolTip({
                                                                    id: cell.row.values.sqlhash,
                                                                    position: getToolTipPosition(event)
                                                                })
                                                            }
                                                            onMouseLeave={() =>
                                                                setVisibleToolTip({ id: '', position: 0 })
                                                            }
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {cell.render('Cell')}
                                                            {cell.row.values.sqlhash === visibleToolTip.id && (
                                                                <StatementToolTip
                                                                    instanceType={getInstanceType(cell.row.original.instanceId)}
                                                                    position={visibleToolTip.position}
                                                                    id={cell.row.values.sqlhash}
                                                                    link={`/instances/${cell.row.original.instanceId}/activity/statement/${cell.row.values.sqlhash}/text`}
                                                                    cell={cell}
                                                                />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        cell.render('Cell')
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                        <ConditionalRender if={isExpanded}>
                                            <tr>
                                                <td colSpan={options.length + 4} className="position-relative">
                                                    <SingleStatementChart
                                                            generateId={props.generateId}
                                                            rowValues={row.values}
                                                            plansLoading={!executionPlansLoaded.includes(row.id)}
                                                            interval={aggregateInt}
                                                            combinedChanges={allChanges}
                                                            instanceOptions={options}
                                                            rowOriginal={row.original}
                                                    />
                                                </td>
                                            </tr>
                                        </ConditionalRender>

                                        {isExpanded && (
                                                <tr>
                                                    <td colSpan={options.length + 4}
                                                        className="expanded-row position-relative">
                                                        <StatementExecutionPlans
                                                                interval={aggregateInt}
                                                                statementsData={statementsData}
                                                                waitTimes={props.waitTimes}
                                                                instanceOptions={options}
                                                                setExecutionPlans={(plans: ExecutionPlan[][]) => setPlans(plans, row.id)}
                                                        rowValues={row.values}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    </ConditionalRender>
                    <ConditionalRender if={loading}>
                        <div className="w-100 text-center text-muted my-3 statement-chart">
                            <div className="loader spinner">
                            </div>
                            <p className="mt-3">
                                Loading data...
                            </p>
                        </div>
                    </ConditionalRender>
                </div>
            </div>
        </div>
    )
}

export default StatementsTable;